import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { getDomElement, getFocusableElements } from '@huggg/bar-staff';
import classnames from 'classnames';
import dict from 'config/dictionary';
import {
  CloseCross,
  PrimaryButton,
  ButtonArrowState,
  Heading_1 as Heading1
} from '@huggg/react-components';
import disableBodyScroll from '../../../utilities/disableBodyScroll.js';
import styles from './dialog.module.scss';

class Dialog extends Component {
  constructor(props) {
    super(props);

    this.keyMap = {
      ESCAPE: 27,
      TAB: 9
    };

    this.state = {
      visible: Boolean(props.visible)
    };

    this.dialogRef = createRef();
    this._enableScroll = null;
    this.rootElement = getDomElement(props.applicationRootElement);
    this.close = this.close.bind(this);
    this.getClassNames = this.getClassNames.bind(this);
    this.onBeforeOpen = this.onBeforeOpen.bind(this);
    this.onAfterOpen = this.onAfterOpen.bind(this);
    this.onAfterClose = this.onAfterClose.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.onWindowKeyup = this.onWindowKeyup.bind(this);
    this.onWindowKeydown = this.onWindowKeydown.bind(this);
    this.open = this.open.bind(this);
    this.renderDialog = this.renderDialog.bind(this);
    this.registerEventListeners = this.registerEventListeners.bind(this);
    this.trapFocus = this.trapFocus.bind(this);
    this.unregisterEventListeners = this.unregisterEventListeners.bind(this);
    this.eventListeners = [];
    this.focusableElements = [];
  }

  disableScroll() {
    // reset first in case we're called twice...
    this._enableScroll && this._enableScroll();
    // then disable the scroll
    this._enableScroll = disableBodyScroll();
  }

  enableScroll() {
    // enable if we haven't yet
    this._enableScroll && this._enableScroll();
    // prevent double-enabling as this will mess with the users scroll
    this._enableScroll = null;
  }

  componentDidMount() {
    this.rootElement.setAttribute('aria-hidden', false);
  }

  componentWillUnmount() {
    this.unregisterEventListeners();
    this.enableScroll();
    this.rootElement.setAttribute('aria-hidden', false);
  }

  close() {
    const { openButtonRef } = this.props;

    this.setState({ visible: false });
    this.unregisterEventListeners();
    this.focusableElements = [];
    this.props.onClose && this.props.onClose();

    if (openButtonRef && openButtonRef.current) {
      openButtonRef.current.focus();
    }
  }

  open() {
    this.setState({ visible: true });
  }

  onBeforeOpen() {
    this.rootElement.setAttribute('aria-hidden', true);
    this.disableScroll();
  }

  onAfterOpen() {
    this.focusableElements = getFocusableElements(this.dialogRef.current);
    this.focusableElements[0] && this.focusableElements[0].focus();
    this.registerEventListeners();

    this.props.onAfterOpen && this.props.onAfterOpen();
  }

  onAfterClose() {
    this.enableScroll();
    this.rootElement.setAttribute('aria-hidden', false);

    this.props.onAfterClose && this.props.onAfterClose();
  }

  onOverlayClick() {
    const { disableOverlayClick } = this.props;

    if (!disableOverlayClick) {
      this.close();
    }
  }

  onWindowKeydown(event) {
    if (this.state.visible) {
      this.trapFocus(event);
    }
  }

  onWindowKeyup(event) {
    const code = event.keyCode || event.which;

    if (this.state.visible) {
      if (code === this.keyMap.ESCAPE) {
        this.close();
        event.stopPropagation();
      }
    }
  }

  trapFocus(event) {
    const code = event.keyCode || event.which;
    const isShift = event.shiftKey;

    this.focusableElements = getFocusableElements(this.dialogRef.current);

    if (code === this.keyMap.TAB) {
      if (isShift) {
        if (document.activeElement === this.focusableElements[0]) {
          event && event.preventDefault();
          this.focusableElements[this.focusableElements.length - 1].focus();
        }
      } else {
        if (
          document.activeElement ===
          this.focusableElements[this.focusableElements.length - 1]
        ) {
          event && event.preventDefault();
          this.focusableElements[0].focus();
        }
      }
    }
  }

  registerEventListeners() {
    window.addEventListener('keyup', this.onWindowKeyup);
    window.addEventListener('keydown', this.onWindowKeydown);

    this.eventListeners.push(
      {
        eventName: 'keyup',
        listener: this.onWindowKeyup,
        target: window
      },
      {
        eventName: 'keydown',
        listener: this.onWindowKeydown,
        target: window
      }
    );
  }

  unregisterEventListeners() {
    this.eventListeners.forEach(eventListener => {
      const { eventName, listener, target } = eventListener;

      target.removeEventListener(eventName, listener);
    });

    this.eventListeners = [];
  }

  getClassNames() {
    return {
      dialog: {
        enter: styles.dialogEnter,
        enterActive: styles.dialogEnterActive,
        exit: styles.dialogExit,
        exitActive: styles.dialogExitActive
      },
      overlay: {
        enter: styles.overlayEnter,
        enterActive: styles.overlayEnterActive,
        exit: styles.overlayExit,
        exitActive: styles.overlayExitActive
      }
    };
  }

  renderDialog() {
    const {
      closeButton: ButtonComponent,
      applicationRootElement,
      ariaDescribedBy,
      ariaLabelledBy,
      children,
      closeButtonText,
      contentClassName,
      openButtonRef,
      showCloseButton,
      title,
      onAfterOpen: _onAfterOpen,
      onAfterClose: _onAfterClose,
      visible,
      hidden = false,
      ...rest
    } = this.props;

    delete rest.disableOverlayClick;
    delete rest.domNode;

    const shouldShow = visible === undefined ? this.state.visible : visible;

    return (
      <div style={{ opacity: hidden ? 0 : 1 }}>
        <TransitionGroup component={null}>
          {shouldShow ? (
            <CSSTransition
              classNames={this.getClassNames().overlay}
              timeout={hidden ? 0 : 300}
            >
              <div
                className={styles.overlay}
                onClick={this.onOverlayClick}
                data-test-id="dialog-overlay"
              />
            </CSSTransition>
          ) : null}

          {shouldShow ? (
            <CSSTransition
              classNames={this.getClassNames().dialog}
              onEnter={this.onBeforeOpen}
              onEntered={this.onAfterOpen}
              onExited={this.onAfterClose}
              timeout={
                hidden ? { enter: 0, exit: 0 } : { enter: 500, exit: 300 }
              }
            >
              <div
                aria-labelledby={ariaLabelledBy}
                aria-describedby={ariaDescribedBy}
                className={styles.dialog}
                ref={this.dialogRef}
                role="dialog"
                {...rest}
              >
                <div className={classnames(styles.content, contentClassName)}>
                  {title && (
                    <div className={styles['header-redesign']}>
                      <Heading1
                        className={styles['confirmationTitle-redesign']}
                      >
                        {title}
                      </Heading1>

                      {showCloseButton && (
                        <div
                          className={styles['close-cross']}
                          onClick={this.close}
                        >
                          <CloseCross />
                        </div>
                      )}
                    </div>
                  )}

                  {children}

                  {/* If we have a title, put the close button in the header */}
                  {showCloseButton &&
                    !title &&
                    React.cloneElement(ButtonComponent, {
                      onClick: this.close,
                      label: closeButtonText
                        ? closeButtonText
                        : dict.closeDialog,
                      arrow: ButtonArrowState.None
                    })}
                </div>
              </div>
            </CSSTransition>
          ) : null}
        </TransitionGroup>
      </div>
    );
  }

  render() {
    return ReactDOM.createPortal(this.renderDialog(), this.props.domNode);
  }
}

Dialog.defaultProps = {
  applicationRootElement: '#root',
  closeButton: <PrimaryButton style={{ width: '230px', margin: '0 auto' }} />,
  disableOverlayClick: false,
  domNode: document.body,
  showCloseButton: true
};

Dialog.propTypes = {
  ariaLabelledBy: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
  applicationRootElement: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node
  ]),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  closeButton: PropTypes.element,
  closeButtonText: PropTypes.string, // in place of a button element
  contentClassName: PropTypes.string,
  disableOverlayClick: PropTypes.bool,
  domNode: PropTypes.instanceOf(Element),
  openButtonRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  showCloseButton: PropTypes.bool,
  onAfterOpen: PropTypes.func,
  onAfterClose: PropTypes.func,
  // Injecting a title automatically triggers new-style dialog layout
  title: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string
  ]),
  onClose: PropTypes.func, // alterantive to the ref handling
  visible: PropTypes.bool, // alterantive to the ref handling
  hidden: PropTypes.bool
};

export default Dialog;
